import {
    connect,
    injectReducer,
    injectSaga,
    eventHelper,
    RootState,
} from '@vfde-fe/sails_core';
import { createStructuredSelector } from 'reselect';
import './style.scss';
import { CONTAINER_TRADEIN } from 'Constant';
import { IInitialState as IInitialStateTradeIn, StateProps } from './interfaces/state';
import tradeInSaga from './saga';
import tradeInReducer from './reducer';
import {
    selectIsDeviceNotFound,
    selectDevices,
    selectHasError,
    selectTradeIn,
    selectLoading,
    selectSelectedDeviceId,
    selectSuggestFieldValue,
    selectTradeInSwitch,
} from './selectors';
import {
    tradeInActionDispatchers,
    TradeInActionDispatchers,
} from './actions';
import { areArraysEqual } from './helpers/areArraysEqual';
import { mountSwitch } from '../../components/mountSwitch';
import {
    HARDWARE_TRADE_IN_HARDWARE_ONLY_NOTIFICATION,
    HARDWARE_TRADE_IN_PROMO_TILE,
    HARDWARE_TRADE_IN_SUMMARY_TILE,
    TAUSCHBONUS_PROMO_TILE,
    TRADEIN_SWITCH,
} from './constants';
import { mountTauschbonusPromoTile, mountTradeInPromoTile, updateTradeInPromoTile } from '../../components/mountPromoTile';
import { mountTradeInSummaryTile, updateTradeInSummaryTile } from '../../components/mountSummaryTile';
import { getTradeIn } from './helpers/getCustomerConfig';
import { toggleElement } from '../../helpers/domHelper';
import { mountNotification } from '../../components/mountNotification';
import { getIsTradeInFromDom, setTradeInSwitch } from './helpers/domHelper';
import { getDeviceFromStorage } from './helpers/getDeviceFromStorage';
import { CLASSNAME_HIDDEN } from '@vfde-brix/core';
import { selectDevice, selectHardwareOnly } from '../DeviceDetail/selectors';
import { IInitialState as IInitialStateDeviceDetail } from '../DeviceDetail/interfaces/state';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-fe/sails-constants';

function TradeIn (state: StateProps, actions: TradeInActionDispatchers) {
    const {
        createInitialState,
        toggleTradeInSwitch,
        triggerOverlay,
    } = actions;

    injectReducer(CONTAINER_TRADEIN, tradeInReducer);
    injectSaga(CONTAINER_TRADEIN, tradeInSaga);
    createInitialState();

    mountSwitch(TRADEIN_SWITCH, toggleTradeInSwitch, getTradeIn(), true);
    const promoTile = mountTradeInPromoTile(actions)!;
    const summaryTile = mountTradeInSummaryTile(actions)!;

    const tauschbonusOptions = window[ADDITIONAL_PAGE_OPTIONS].promos?.tauschbonus;
    const tauschbonusPromoTile = document.getElementById(TAUSCHBONUS_PROMO_TILE);
    (tauschbonusOptions && tauschbonusPromoTile) && mountTauschbonusPromoTile(tauschbonusPromoTile, tauschbonusOptions);
    tauschbonusPromoTile && tauschbonusPromoTile.classList.toggle(CLASSNAME_HIDDEN, state.hardwareOnly);

    mountNotification(HARDWARE_TRADE_IN_HARDWARE_ONLY_NOTIFICATION);

    window.addEventListener('pageshow', e => {
        // on cached pageshow (e.g. afer forwards or backwards navigation)
        // check if the user enabled the tradeIn switch but did not select
        // a device so we deactivate the switch and hide the promo tile
        if (e.persisted && getIsTradeInFromDom() && !getDeviceFromStorage()?.id) {
            setTradeInSwitch(false);
            toggleElement(HARDWARE_TRADE_IN_PROMO_TILE, true);
            toggleTradeInSwitch(false);
        }
    });

    return {
        getDerivedStateFromProps (newState: StateProps, oldState: StateProps) {
            const { payload, selectedDeviceId, isTradeIn, hasError, isDeviceNotFound, loading, tradeInSwitch, device } = newState;

            if (loading) {
                promoTile.getSuggestFiledComponent().getLoadingAnimationComponent().show();
            } else {
                promoTile.getSuggestFiledComponent().getLoadingAnimationComponent().hide();
            }

            if (!areArraysEqual(payload, oldState.payload)) {
                // If the API results has changed compairing to the previous ones
                updateTradeInPromoTile(promoTile, newState, true);
            } else if (hasError !== oldState.hasError || isDeviceNotFound !== oldState.isDeviceNotFound) {
                // Else if an error occured or a device not found, re-render promo tile so we show notifications - or the opposite
                updateTradeInPromoTile(promoTile, newState, true);
            }

            if (device && device !== oldState.device) {
                updateTradeInSummaryTile(summaryTile, newState);
            }

            if (selectedDeviceId && selectedDeviceId !== oldState.selectedDeviceId) {
                // If a device is selected, update summary tile with the new device values and update promo tile, also trigger the overlay initialization
                updateTradeInSummaryTile(summaryTile, newState);
                updateTradeInPromoTile(promoTile, newState, false);
                eventHelper(triggerOverlay);
            }

           if (isTradeIn) {
                // In case of the trade-in switch is on, and there's a selected device, Hide PromoTile and Show SummaryTile
                toggleElement(HARDWARE_TRADE_IN_PROMO_TILE, true);
                toggleElement(HARDWARE_TRADE_IN_SUMMARY_TILE, false);
            }

            if (!selectedDeviceId) {
                if (selectedDeviceId !== oldState.selectedDeviceId) {
                    // If the selected device is removed, update promo tile so it's re-rendered
                    updateTradeInPromoTile(promoTile, newState, false);
                }

                if (tradeInSwitch) {
                    // In case of the trade-in switch is on, and there's not a selected device, Show PromoTile and Hide SummaryTile
                    toggleElement(HARDWARE_TRADE_IN_PROMO_TILE, false);
                    toggleElement(HARDWARE_TRADE_IN_SUMMARY_TILE, true);
                }

                if (!tradeInSwitch && oldState.tradeInSwitch) {
                    // reset error notification and input value by re-rendering the promo-tile when tradein is switched off
                    updateTradeInPromoTile(promoTile, newState, false);
                }
            }

            if (!tradeInSwitch) {
                // In case of the trade-in switch is off, but it still could be there a device selected, Hide PromoTile and Hide SummaryTile
                toggleElement(HARDWARE_TRADE_IN_PROMO_TILE, true);
                toggleElement(HARDWARE_TRADE_IN_SUMMARY_TILE, true);
            }
        },
    };
}

const mapStateToProps = createStructuredSelector<RootState<IInitialStateTradeIn & IInitialStateDeviceDetail>, StateProps>({
    loading: selectLoading(),
    hasError: selectHasError(),
    payload: selectDevices(),
    isTradeIn: selectTradeIn(),
    tradeInSwitch: selectTradeInSwitch(),
    suggestFieldValue: selectSuggestFieldValue(),
    selectedDeviceId: selectSelectedDeviceId(),
    isDeviceNotFound: selectIsDeviceNotFound(),
    device: selectDevice(),
    hardwareOnly: selectHardwareOnly(),
});

const mountTradeInContainer = connect(mapStateToProps, tradeInActionDispatchers)(TradeIn);

export default mountTradeInContainer;
